import { gql } from '@apollo/client';
import { Checkbox, Col, Form, Row, Select, Input, Alert, Button, Space } from 'antd';
import { useMutation, useQuery } from 'hooks/apollo';
import useUser from 'hooks/useUser';
import useExternalAIConfirmation from 'hooks/useExternalAIConfirmation';
import React, { useEffect, useState } from 'react';
import { DEFAULT_MODEL, MODELS, TAG_RESPONSES_SYSTEM_PROMPT } from 'shared/constants/ai';

const { TextArea } = Input;

const autoTagDefaultValues = {
  sentiment: true,
  translate: true,
};

const TAG_COMMENTS = gql`
  mutation($input: AutoTagCommentsInput!, $ongoing: Boolean) {
    autoTagComments(input: $input, ongoing: $ongoing)
  }
`;

const GET_AI_DEFAULTS = gql`
  query($keys: [String!]) {
    settings(keys: $keys)
  }
`;

const AutoTagComments = ({
  onDone,
  tags,
  sentiments,
  questionIds,
  questionResponseIds,
  hasOngoingAutoTagging = false,
}) => {
  const { isSuperUser } = useUser();
  const confirmExternalAI = useExternalAIConfirmation();
  const [form] = Form.useForm();
  const [submitMode, setSubmitMode] = useState('current');
  const [tagMutation, { loading }] = useMutation(TAG_COMMENTS);
  const { data: aiDefaultsData } = useQuery(GET_AI_DEFAULTS, {
    variables: { keys: ['aiDefaultModel', 'aiAutoTaggingSystemPrompt'] },
    skip: !isSuperUser,
  });
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const defaultModelName = aiDefaultsData?.settings?.aiDefaultModel || DEFAULT_MODEL;
  const defaultSystemPrompt = aiDefaultsData?.settings?.aiAutoTaggingSystemPrompt || TAG_RESPONSES_SYSTEM_PROMPT;

  useEffect(() => {
    form.setFieldValue('tagIds', tags.map((tag) => tag.id));
  }, [tags]);

  useEffect(() => {
    if (!isSuperUser) {
      return;
    }

    if (!form.getFieldValue('modelName')) {
      form.setFieldValue('modelName', defaultModelName);
    }

    if (!form.getFieldValue('systemPrompt')) {
      form.setFieldValue('systemPrompt', defaultSystemPrompt);
    }
  }, [isSuperUser, defaultModelName, defaultSystemPrompt, form]);

  const onSubmit = async (values) => {
    if (!await confirmExternalAI()) return;
    const ongoing = submitMode === 'ongoing';
    const input = {
      ...values,
      questionIds,
      questionResponseIds,
      sentimentIds: values.sentiment ? sentiments.map((sentiment) => sentiment.id) : null,
    };
    await tagMutation({ variables: { input, ongoing } });
    onDone();
  };

  return (
    <Form form={form} layout="vertical" initialValues={{ ...autoTagDefaultValues, modelName: defaultModelName, systemPrompt: defaultSystemPrompt }} onFinish={onSubmit}>
      <Form.Item>
        <Alert message="This will send the text responses to an external service and will replace any existing AI applied tags." type="warning" showIcon />
      </Form.Item>
      <Form.Item name="tagIds" label={`Tags (${tags.length})`} rules={[{ required: true, message: 'Select atleast one tag' }]}>
        <Select options={tags.map((theme) => ({ value: theme.id, label: theme.text }))} mode="multiple" allowClear />
      </Form.Item>
      <Row gutter={16}>
        <Col>
          <Form.Item name="sentiment" valuePropName="checked">
            <Checkbox>Sentiment analysis</Checkbox>
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name="translate" valuePropName="checked">
            <Checkbox>Translate to English</Checkbox>
          </Form.Item>
        </Col>
      </Row>
      {isSuperUser && (
        <Form.Item>
          <Form.Item name="customInstructions" label="Custom instructions">
            <TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
          <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setShowAdvancedSettings((prev) => !prev)}>
            {showAdvancedSettings ? 'Hide advanced settings' : 'Show advanced settings'}
          </Button>
          {showAdvancedSettings && (
            <>
              <Form.Item name="modelName" label="Model">
                <Select
                  options={MODELS.map((model) => ({
                    label: model === defaultModelName ? `${model} (default)` : model,
                    value: model,
                  }))}
                />
              </Form.Item>
              <Form.Item name="systemPrompt" label="System prompt override">
                <TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            </>
          )}
        </Form.Item>
      )}
      <Space>
        <Button
          onClick={() => {
            setSubmitMode('ongoing');
            form.submit();
          }}
          loading={loading}
          disabled={hasOngoingAutoTagging}
        >
          Create ongoing AI tagging
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setSubmitMode('current');
            form.submit();
          }}
          loading={loading}
        >
          {`Tag ${questionResponseIds.length} responses now`}
        </Button>
      </Space>
    </Form>
  );
};

export default AutoTagComments;
